import {
  alpha,
  Box,
  keyframes,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

export const CardRoot = styled(Stack, { name: 'PolicyCardRoot' })({
  position: 'relative',
  flexDirection: 'row',
  borderRadius: '0.625rem',
  overflow: 'hidden',
  margin: '0 0 0.5rem',
  border: '1px solid transparent',
  cursor: 'pointer',
});

// colour is set inline at render time from getPolicyColor
export const CardRail = styled(Box, { name: 'PolicyCardRail' })({
  width: '0.25rem',
  flexShrink: 0,
});

export const CardContent = styled(Box, { name: 'PolicyCardContent' })({
  flex: 1,
  minWidth: 0,
  padding: '0.625rem 0.375rem 0.625rem 0.75rem',
});

export const RowHead = styled(Stack, { name: 'PolicyCardRowHead' })({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.5rem',
  marginBottom: '0.1875rem',
  cursor: 'pointer',
  minHeight: '1.9375rem',
});

export const Dot = styled(Box, { name: 'PolicyCardDot' })({
  width: '0.5625rem',
  height: '0.5625rem',
  borderRadius: '50%',
  flexShrink: 0,
  // optically center with the first line of a wrapping title
  marginTop: '0.3125rem',
});

export const PolicyName = styled(Typography, { name: 'PolicyCardName' })(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '-0.005em',
  lineHeight: 1.35,
  color: theme.palette.custom.OV.sys.onBackground,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
}));

export const chevronSx =
  (expanded: boolean): SxProps<Theme> =>
  (theme) => ({
    fontSize: '1.125rem',
    marginLeft: '0.125rem',
    marginTop: '0.125rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    transition: 'transform .15s',
    transform: expanded ? 'rotate(180deg)' : 'none',
    flexShrink: 0,
  });

export const KebabSlot = styled(Stack, { name: 'PolicyCardKebabSlot' })(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  flexShrink: 0,
  marginTop: '0.125rem',

  '& .MuiIconButton-root': {
    width: '1.375rem',
    height: '1.375rem',
    padding: 0,
    borderRadius: '0.25rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
}));

// a div, not Typography: its polymorphic `component` typing breaks under styled()
export const Description = styled('div', { name: 'PolicyCardDescription' })(({ theme }) => ({
  fontSize: '0.71875rem',
  letterSpacing: 'normal',
  color: theme.palette.custom.OV.sys.onSurface,
  lineHeight: 1.45,
  marginBottom: '0.25rem',
}));

export const TagRow = styled(Box, { name: 'PolicyCardTagRow' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3125rem',
  fontSize: '0.6875rem',
  letterSpacing: 'normal',
  lineHeight: 'normal',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

export const getValidityChipSx =
  (isValidFuture: boolean): SxProps<Theme> =>
  (theme) => ({
    borderColor: isValidFuture
      ? theme.palette.custom.OV.sys.success
      : theme.palette.custom.OV.sys.error,
    color: isValidFuture
      ? theme.palette.custom.OV.sys.success
      : theme.palette.custom.OV.sys.error,
    height: '1.25rem',
    fontSize: '0.6875rem',
    backgroundColor: 'transparent',
    fontWeight: 500,
  });

export const Meta = styled(Typography, { name: 'PolicyCardMeta' })(({ theme }) => ({
  fontSize: '0.65625rem',
  letterSpacing: 'normal',
  lineHeight: 'normal',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontStyle: 'italic',
  marginTop: '0.625rem',
}));

const cardFocus = keyframes`
  from { box-shadow: 0 0 0 0.1875rem var(--policy-focus-ring); }
  to { box-shadow: 0 0 0 0 transparent; }
`;

export const getCardFocusSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '--policy-focus-ring': alpha(theme.palette.custom.OV.sys.secondary, 0.4),
  animation: `${cardFocus} 1.2s ease-out .12s both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
